import { Filing } from "../models/Filing";
import { FilingSection } from "../models/FilingSection";
import { clean } from "../utils/utils";
import api from "./api"; // your custom Axios instance

const PATH = "/api/v1/filings";

export const FilingService = {
  async createFiling(
    file: globalThis.File | null,
    filingData: Partial<
      Omit<Filing, "_id" | "createdAt" | "updatedAt" | "createdBy" | "sections">
    >,
  ): Promise<string> {
    const formData = new FormData();
    formData.append("matter", filingData.matter!);
    if (file) formData.append("file", file);
    if (filingData.name) formData.append("name", filingData.name);
    if (filingData.description)
      formData.append("description", filingData.description);
    if (filingData.side) formData.append("side", filingData.side);
    const res = await api.post(`${PATH}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data.data._id;
  },

  async getFiling(filingId: string): Promise<Filing> {
    const response = await api.get(`${PATH}/${filingId}`);
    return response.data.data;
  },

  async getDownloadURL(filingId: string): Promise<string> {
    const response = await api.get(`${PATH}/${filingId}/download`);
    return response.data.data.url;
  },

  async getFilingsByMatter(matterId: string): Promise<Filing[]> {
    const response = await api.get(`${PATH}?matterId=${matterId}`);
    return response.data.data || [];
  },

  async updateFiling(
    filingId: string,
    updatedData: Partial<Omit<Filing, "_id" | "createdAt">>,
  ): Promise<void> {
    await api.put(`${PATH}/${filingId}`, clean(updatedData));
  },

  async reorderSections(filingId: string, sectionIds: string[]): Promise<void> {
    await api.post(`${PATH}/${filingId}/sections/reorder`, { sectionIds });
  },

  async deleteFiling(filingId: string): Promise<void> {
    await api.delete(`${PATH}/${filingId}`);
  },

  async getComments(filingId: string): Promise<Comment[]> {
    const response = await api.get(`${PATH}/${filingId}/comments`);
    return response.data.data || [];
  },

  // Section
  async addSection(filingId: string, section: FilingSection): Promise<string> {
    const response = await api.post(`${PATH}/${filingId}/sections`, section);
    return response.data.data._id;
  },

  async updateSection(
    filingId: string,
    sectionId: string,
    updatedSection: Partial<FilingSection>,
  ): Promise<void> {
    await api.put(
      `${PATH}/${filingId}/sections/${sectionId}`,
      clean(updatedSection),
    );
  },

  async deleteSection(filingId: string, sectionId: string): Promise<void> {
    await api.delete(`${PATH}/${filingId}/sections/${sectionId}`);
  },

  // Comment
  async addComment(
    filingId: string,
    sectionNumber: string,
    comment: {
      content: string;
    },
  ): Promise<void> {
    await api.post(
      `${PATH}/${filingId}/sections/${sectionNumber}/comments`,
      comment,
    );
  },
  async updateComment(
    filingId: string,
    commentId: string,
    updatedComment: Partial<Comment>,
  ): Promise<void> {
    await api.put(
      `${PATH}/${filingId}/comments/${commentId}`,
      clean(updatedComment),
    );
  },

  async deleteComment(
    filingId: string,
    sectionId: string,
    commentId: string,
  ): Promise<void> {
    await api.delete(
      `${PATH}/${filingId}/sections/${sectionId}/comments/${commentId}`,
    );
  },
};
